import traverse from 'traverse';

import {unitlessKeys} from './unitlessKeys';

import type {CreateVars, GenerateVars, TokensObject} from './createVars.types';

/**
 * Converts numbers to pixels, except in case of reserved unitless keywords.
 *
 * > **Note**:
 * > This function keys into the specific words for these values.
 * > For a list of reserved keywords for unitless values, see [`unitlessKeys.js`](./unitlessKeys.js)
 */
// @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
const formatValue = (value, shouldConvertToPixels = false, path = []) => {
  const containsUnitlessKeyword = path.some((e) => unitlessKeys[e]);

  const convertValue =
    typeof value === 'number' &&
    shouldConvertToPixels &&
    !containsUnitlessKeyword;

  return convertValue ? `${value}px` : value;
};

/**
 * Converts an arbitrarily nested theme mapping into a set of stable tokens and CSS Vars assignments.
 */
const generateVars: GenerateVars = (
  mapping = {},
  {prefix = 'local', shouldConvertToPixels = false} = {}
) => {
  if (Object.keys(mapping).length === 0)
    return {
      cssVars: {},
      localTokens: {},
    };

  const cssVars: Record<string, any> = {};

  const localTokens = traverse(mapping).map(function (value) {
    if (this.isLeaf) {
      if (typeof value === 'function') {
        throw new Error(
          `Please do not use functions located in legacy themes in createLegacyVars.
          This is non-standard behavior and is being actively deprecated.
          If you find the need to access these functions in emotionStyled, please contact the Design System team.
          `
        );
      }

      const varName = `--${prefix}-${this.path.join('-')}`;
      const formattedValue = formatValue(
        value,
        shouldConvertToPixels,
        // @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'never[]'.
        this.path
      );
      cssVars[varName] = formattedValue;
      this.update(
        `var(${varName}${formattedValue ? `, ${formattedValue}` : ''})`
      );
    }
  });

  return {cssVars, localTokens};
};

/**
 * `createVars` is a helper function used to set up and modify CSS vars for local use.
 * It is primarily a tool for Design Systems to create Library level components.
 *
 * It accepts a style object that will define the vars and set their fallback value. If a style is set to `null`, then no fallback value will be set.
 *
 * ```javascript
 * // Example usage
 * const [localTokens, setLocalTokens] = createVars({ padding: 15, margin: null }) // cssVars === { padding: 'var(--local-padding, 15)', margin: 'var(--local-margin)' }
 *
 * const SomeComponent = emotionStyled('div')({ ...cssVars, otherCSS: 'here' })
 *
 * const Usage = () => {
 *  const style = setLocalTokens(24)
 *
 *  return (
 *    <>
 *      <SomeComponent>I have padding of 15</SomeComponent
 *      <SomeComponent style={style}>I have padding of 24</SomeComponent>
 *    </>
 *  )
 * }
 * ```
 *
 * @param {Object} customVars a Style Object that will be converted into CSS vars.
 * @param {Object} Options
 * @property {string} `Options.prefix` - a custom prefix added to the CSS Vars returned by createVars.
 * @returns {Object} An object containing custom CSS Vars and the `setLocalTokens` function to locally reassign CSS Vars.
 */
export const createVars: CreateVars = (
  customVars = {},
  {prefix = 'local', shouldConvertToPixels = false} = {}
) => {
  const {cssVars, localTokens} = generateVars(customVars, {
    prefix,
    shouldConvertToPixels,
  });

  /**
   * `setLocalTokens` is a helper function to assign new values to the CSS Vars created by the createVars() function. It will not set values not created with the original `createVars` function.
   *
   * ```javascript
   * const [localTokens, setLocalTokens] = createVars({ padding: '12px' })
   * const newVars = setLocalTokens({ gridGap: '16px', color: 'blue' }) // returns { `--local-grid-gap`: '16px', `--local-padding`: '12px'}
   *
   * // use by adding as an inline style
   * <SomeComponent style={newVars} />
   * ```
   *
   * @param {Object} newVars A set of CSS vars and their new values.
   * @returns {Object} A Style Object of CSS Vars and their new values.
   */
  const setLocalTokens = (
    newVars: undefined | TokensObject,
    {setAll = true} = {}
  ) => {
    // Start with a copy of the existing cssVars object.
    const restyledVars = setAll ? {...cssVars} : {};

    traverse(newVars).forEach(function (value) {
      if (this.isLeaf && value) {
        const varName = `--${prefix}-${this.path.join('-')}`;
        if (cssVars[varName]) {
          restyledVars[varName] = formatValue(
            value,
            shouldConvertToPixels,
            // @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'never[]'.
            this.path
          );
        }
      }
    });

    return restyledVars;
  };

  return [localTokens, setLocalTokens];
};
